// The dashboard's tailoring box, built like the JobGPT composer. Every setting
// is a button in the box's bottom row; the arrow unlocks once a resume and a
// job description are in. Pressing it hands the settings to the tailoring
// engine (lib/tailoringQuickStart) and shows progress right here.
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  ArrowUp, Briefcase, Check, ChevronDown, ClipboardList, FileText, Files, Loader2,
  Lock, MessageSquare, Paperclip, Settings2, SlidersHorizontal, Target, Upload, X, CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { ResumeService, type Resume } from "@/services/resumeService";
import { JobLibraryService, type SavedJobDescription } from "@/services/jobLibraryService";
import { ResumeLibraryService } from "@/services/resumeLibraryService";
import { TailoredResumeService } from "@/services/tailoredResumeService";
import { tailoringJdFromSavedJob, completeJdFromSavedJob } from "@/lib/savedJobJd";
import {
  requestQuickStart, subscribeQuickStartPhase, getQuickStartPhase,
  type QuickStartDepth, type QuickStartMode, type QuickStartPhase,
} from "@/lib/tailoringQuickStart";
import { TailoringSteps } from "@/components/dashboard/TailoringSteps";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";

const MIN_JD_CHARS = 50;

// Paste auto-detect (Marcus, Oct 25 2026): pasting a RESUME into the box
// attaches it as the resume; pasting a JOB DESCRIPTION keeps it as the job;
// pasting both at once splits them. Pure text signals, no API call.
const RESUME_HEADINGS = /^\s*(professional\s+experience|work\s+experience|experience|employment\s+history|work\s+history|education|skills|core\s+competencies|areas\s+of\s+expertise|career\s+highlights|professional\s+summary|career\s+summary|summary|certifications?(\s*&\s*licenses)?|licenses?|technical\s+skills|awards(\s*&\s*honors)?)\s*:?\s*$/im;
const JD_MARKERS = /(^|\n)\s*(job\s+description|about\s+the\s+(role|job|position|company)|the\s+role|responsibilities|key\s+responsibilities|duties|what\s+you('|’)ll\s+do|what\s+we('|’)re\s+looking\s+for|requirements|minimum\s+qualifications|preferred\s+qualifications|qualifications|who\s+you\s+are|benefits|compensation|salary\s+range|pay\s+range|equal\s+opportunity\s+employer)\b/gi;
function resumeScore(t: string): number {
  let n = 0;
  if (/[\w.+-]+@[\w-]+\.[\w.]+/.test(t)) n += 2;
  if (/(\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4})/.test(t)) n += 2;
  if (/linkedin\.com\/in\//i.test(t)) n += 2;
  n += Math.min(4, (t.match(new RegExp(RESUME_HEADINGS.source, "gim")) ?? []).length);
  n += Math.min(4, (t.match(/\b(19|20)\d{2}\s*(-|–|—|to)\s*((19|20)\d{2}|present|current)\b/gi) ?? []).length);
  return n;
}
function jdScore(t: string): number {
  let n = Math.min(6, (t.match(JD_MARKERS) ?? []).length * 2);
  if (/\b(you\s+will|we\s+are|our\s+team|join\s+us|the\s+ideal\s+candidate|apply)\b/i.test(t)) n += 2;
  return n;
}
/** Classify a pasted chunk: resume, job, both (with the split point) or unknown. */
function classifyPaste(t: string): { kind: "resume" | "job" | "both" | "unknown"; resume?: string; job?: string } {
  const r = resumeScore(t), j = jdScore(t);
  const isResume = r >= 6, isJob = j >= 4;
  if (isResume && isJob) {
    // Split where a JD block begins: the first JD marker line after which the
    // rest scores as a job and the part before scores as a resume.
    const re = new RegExp(JD_MARKERS.source, "gi"); let m: RegExpExecArray | null;
    while ((m = re.exec(t))) {
      const cut = m.index;
      const head = t.slice(0, cut), tail = t.slice(cut);
      if (resumeScore(head) >= 6 && jdScore(tail) >= 4) return { kind: "both", resume: head.trim(), job: tail.trim() };
      const head2 = t.slice(cut), tail2 = t.slice(0, cut);
      if (resumeScore(head2) >= 6 && jdScore(tail2) >= 4) return { kind: "both", resume: head2.trim(), job: tail2.trim() };
    }
    return r - 2 > j ? { kind: "resume" } : { kind: "job" };
  }
  if (isResume && r > j) return { kind: "resume" };
  if (isJob) return { kind: "job" };
  return { kind: "unknown" };
}

const TARGETS = [
  { id: "job-description", label: "Job description", ready: true },
  { id: "job-position", label: "Job title and industry", ready: false },
  { id: "institution", label: "Company or organization", ready: false },
  { id: "revisions", label: "Revisions and updates", ready: false },
  { id: "assessment-questions", label: "Assessment answers", ready: false },
] as const;

const DOCUMENTS = [
  { id: "resume", label: "Resume" },
  { id: "cover-letter", label: "Cover letter" },
  { id: "thank-you-letter", label: "Thank you letter" },
  { id: "outreach-letter", label: "Outreach email" },
  { id: "executive-bio", label: "Executive bio" },
  { id: "linkedin-profile", label: "LinkedIn profile" },
  { id: "solution-asset-document", label: "Solution asset", deepOnly: true },
  { id: "hiring-manager-contact-report", label: "Hiring manager contact report", deepOnly: true },
] as const;

const HIGHLIGHT_CHOICES = [0, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const btn =
  "flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-[12px] font-semibold whitespace-nowrap text-slate-500 hover:text-[#000F36] hover:bg-slate-100 transition-all outline-none disabled:opacity-50";
// Setup chips (Marcus, Oct 25 2026): every setting is a clearly named chip.
// Not chosen yet = amber outline saying what to pick. Chosen = green with a
// check mark and "selected" plus what was picked.
// Simple pills (Marcus, Oct 25 2026): short labels, small check when set,
// amber only while something is still needed.
const chipTodo =
  "flex items-center gap-1.5 h-[30px] px-3 rounded-full border border-amber-300 bg-amber-50 text-[12px] font-medium whitespace-nowrap text-amber-800 hover:bg-amber-100 transition-all outline-none disabled:opacity-50";
const chipDone =
  "flex items-center gap-1.5 h-[30px] max-w-[230px] px-3 rounded-full border border-slate-200 bg-white text-[12px] font-medium whitespace-nowrap text-slate-700 hover:bg-slate-50 transition-all outline-none disabled:opacity-50";
const chipOptional =
  "flex items-center gap-1.5 h-[30px] px-3 rounded-full border border-slate-200 bg-white text-[12px] font-medium whitespace-nowrap text-slate-500 hover:bg-slate-50 transition-all outline-none disabled:opacity-50";

export function TailorComposer({ locked, onLocked }: { locked: boolean; onLocked: () => void }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const jdRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [jd, setJd] = useState("");
  const [resume, setResume] = useState<{ name: string; text: string; docxUrl?: string | null; id?: string } | null>(null);
  const designFileRef = useRef<HTMLInputElement>(null);
  // Resume design: RDR Blue, or the client's own design cloned from their Word file.
  const [design, setDesign] = useState<'rdr-blue' | 'original-format'>('rdr-blue');
  const [busy, setBusy] = useState<"" | "reading" | "loading">("");
  const [saved, setSaved] = useState<Resume[] | null>(null);
  const [savedJobs, setSavedJobs] = useState<SavedJobDescription[] | null>(null);
  // A Job Library pick: the box holds its tailoring text; completeJd is what
  // the resume is scored against. Editing the box text breaks the link.
  const [pickedJob, setPickedJob] = useState<{ id: string; title: string; company: string; text: string; completeJd: string } | null>(null);

  const [documents, setDocuments] = useState<string[]>([]);
  const [depth, setDepth] = useState<QuickStartDepth>("quick");
  const [mode, setMode] = useState<QuickStartMode>("Full JD");
  const [highlightCount, setHighlightCount] = useState(5);
  const [generateAnalysis, setGenerateAnalysis] = useState(true);
  const [autoFix, setAutoFix] = useState(true);
  const [instructions, setInstructions] = useState("");
  const [newClientWorksheet, setNewClientWorksheet] = useState("");
  const [quantWorksheet, setQuantWorksheet] = useState("");

  const [phase, setPhase] = useState<QuickStartPhase>(getQuickStartPhase());
  useEffect(() => subscribeQuickStartPhase(setPhase), []);
  useEffect(() => {
    if (phase.phase === "error") toast({ title: "Tailoring stopped", description: phase.message, variant: "destructive" });
  }, [phase, toast]);

  useEffect(() => {
    if (!user?.id) return;
    ResumeService.getUserResumes(user.id).then(({ resumes }) => setSaved(resumes ?? []));
    JobLibraryService.getSavedJobs().then(setSavedJobs).catch(() => setSavedJobs([]));
  }, [user?.id]);

  const pickJob = (job: SavedJobDescription) => {
    const text = tailoringJdFromSavedJob(job);
    setJd(text);
    setPickedJob({ id: job.id, title: job.jobTitle, company: job.companyName, text, completeJd: completeJdFromSavedJob(job) });
    // A saved job's selected items are the ones the member confirmed doing.
    setMode("Personalized JD");
  };

  // Deep-only documents drop off when switching back to Quick.
  useEffect(() => {
    if (depth === "quick") {
      setDocuments((d) => d.filter((id) => !DOCUMENTS.find((x) => x.id === id && "deepOnly" in x)));
    }
  }, [depth]);

  const readFile = async (file: File) => {
    setBusy("reading");
    try {
      const { FileTextExtractor } = await import("@/utils/fileTextExtractor");
      const r = await FileTextExtractor.extractText(file);
      if (!r.success || !r.text?.trim()) throw new Error(r.error || "No text could be read from that file.");
      setResume({ name: file.name, text: r.text });
      // Same as the studio's upload: keep the resume in the library (with its
      // text, so it can be picked again later) and save the "before" version.
      const title = file.name.replace(/.[^/.]+$/, "") || "Untitled Resume";
      if (/\.docx$/i.test(file.name)) {
        // Word files are stored as files too: the "My own design" option
        // rebuilds the resume in this exact design.
        ResumeLibraryService.saveResumeWithFile(title, file)
          .then((savedR: any) => {
            const url = savedR?.originalFileUrl ?? null;
            setResume((cur) => (cur && cur.name === file.name ? { ...cur, docxUrl: url } : cur));
            if (user?.id) ResumeService.getUserResumes(user.id).then(({ resumes }) => setSaved(resumes ?? []));
          })
          .catch((e) => console.error("Failed to save resume to library:", e));
      } else {
        ResumeLibraryService.saveResume(title, r.text, file.name, file.type)
          .then(() => { if (user?.id) ResumeService.getUserResumes(user.id).then(({ resumes }) => setSaved(resumes ?? [])); })
          .catch((e) => console.error("Failed to save resume to library:", e));
      }
      TailoredResumeService.saveBeforeResume({ title, content: r.text, file_name: file.name, file_type: "text/plain" })
        .catch((e) => console.error("Failed to save before resume:", e));
    } catch (e: any) {
      toast({ title: "Couldn't read that resume", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  const pickSaved = async (r: Resume) => {
    setBusy("loading");
    try {
      const { resume: full, error } = await ResumeService.getResume(r.id);
      const text = full?.content?.trim() ?? "";
      if (error || text.length < 50) throw new Error("This resume was saved without its text. Attach the file instead.");
      const fileUrl = (full as any)?.original_file_url ?? (r as any)?.original_file_url ?? null;
      const fname = String((full as any)?.file_name ?? r.file_name ?? fileUrl ?? "");
      setResume({ name: r.title || r.file_name || "Saved resume", text, docxUrl: fileUrl && /\.docx$/i.test(fname) ? fileUrl : null, id: r.id });
    } catch (e: any) {
      toast({ title: "Couldn't load that resume", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  // Resumes saved before Oct 25 2026 kept only their text. Attaching the Word
  // file here stores it on that library resume so "My own design" can copy it.
  const attachDesignFile = async (file: File) => {
    if (!/\.docx$/i.test(file.name)) {
      toast({ title: "Please choose a Word (.docx) file", description: "Your design is copied from the Word file. A PDF cannot be copied.", variant: "destructive" });
      return;
    }
    setBusy("loading");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error("not signed in");
      const path = `${session.user.id}/${Date.now()}.docx`;
      const { error: upErr } = await supabase.storage.from("resumes").upload(path, file, { contentType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", upsert: false });
      if (upErr) throw upErr;
      const url = supabase.storage.from("resumes").getPublicUrl(path).data.publicUrl;
      if (resume?.id) {
        await supabase.from("resumes" as any).update({ original_file_url: url, original_file_path: path }).eq("id", resume.id);
      }
      setResume((cur) => (cur ? { ...cur, docxUrl: url } : cur));
      toast({ title: "Word file attached", description: "Your resume will be built in this design." });
    } catch (e) {
      console.error("Attach design file failed:", e);
      toast({ title: "Sorry, we couldn't attach that file right now", description: "Please try again in a few minutes.", variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  const toggleDoc = (id: string) =>
    setDocuments((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));

  const working = phase.phase === "report" || phase.phase === "starting" || phase.phase === "running";
  const hasJd = jd.trim().length >= MIN_JD_CHARS;
  const designNeedsWord = design === "original-format" && !!resume && !resume.docxUrl;
  const ready = hasJd && !!resume && documents.length > 0 && !busy && !working && !designNeedsWord;
  const missing = [!resume && "add your resume", !hasJd && "paste the job description", documents.length === 0 && "choose the docs to create", designNeedsWord && "attach the Word file in the design menu to use your own design"].filter(Boolean) as string[];

  const submit = () => {
    if (locked) return onLocked();
    if (!ready || !resume) return;
    requestQuickStart({
      target: "job-description",
      jobDescription: jd.trim(),
      jobTitle: pickedJob?.title ?? "",
      completeJd: pickedJob?.completeJd ?? "",
      resumeText: resume.text,
      resumeName: resume.name,
      originalDocxUrl: resume.docxUrl ?? null,
      design,
      documents,
      depth,
      mode,
      highlightCount,
      generateAnalysis,
      autoFixFormatting: autoFix,
      instructions: instructions.trim(),
      newClientWorksheet: newClientWorksheet.trim(),
      quantWorksheet: quantWorksheet.trim(),
    });
  };

  const optionsSet = !!instructions.trim() || !!newClientWorksheet.trim() || !!quantWorksheet.trim();

  return (
    <div>
      {working && <TailoringSteps title={pickedJob ? `Tailoring your resume for ${pickedJob.title}` : "Tailoring your resume"} />}
      <div className={`transition-opacity ${working ? "opacity-60 pointer-events-none" : ""}`}>
      <div className="rounded-2xl border border-[#000F36]/40 bg-white shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-4px_rgba(0,0,0,0.1)] focus-within:border-[#000F36]/70 transition-colors">
        <textarea
          ref={jdRef}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData("text");
            if (!pasted || pasted.trim().length < 200) return;
            const c = classifyPaste(pasted);
            if (c.kind === "both" && c.resume && c.job) {
              e.preventDefault();
              setResume({ name: "Pasted resume", text: c.resume });
              setJd(c.job); setPickedJob(null);
              toast({ title: "Resume and job description detected", description: "The resume was attached and the job description is in the box." });
            } else if (c.kind === "resume" && !resume) {
              e.preventDefault();
              setResume({ name: "Pasted resume", text: pasted.trim() });
              toast({ title: "Resume detected", description: "Your pasted resume was attached. Now paste or pick the job description." });
            } else if (c.kind === "resume" && resume) {
              toast({ title: "This looks like a resume", description: "A resume is already attached. Remove it first if you want to use this one instead." });
            }
          }}
          value={jd}
          disabled={working}
          onChange={(e) => { setJd(e.target.value); if (pickedJob && e.target.value !== pickedJob.text) setPickedJob(null); }}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
          rows={4}
          placeholder="Paste the job description here (or paste your resume and the job together and they are detected automatically), or pick a job from Choose job..."
          className="block w-full resize-none rounded-t-2xl bg-transparent px-5 pt-4 pb-2 text-[15px] leading-relaxed text-[#000F36] placeholder:text-slate-400 outline-none disabled:opacity-60"
        />

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 mx-3 py-2.5">
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) readFile(f); e.target.value = ""; }} />

          {/* Resume */}
          {resume ? (
            <span className={chipDone + " pr-1.5"} title={`Resume selected: ${resume.name}`}>
              <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="truncate">{resume.name}</span>
              <button onClick={() => setResume(null)} disabled={working} aria-label="Remove resume" className="rounded p-0.5 text-slate-400 hover:text-slate-700">
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button disabled={!!busy || working} className={chipTodo}>
                  <Paperclip className="w-3.5 h-3.5" /> Choose Resume
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72 max-h-80 overflow-y-auto font-outfit">
                <DropdownMenuItem onClick={() => fileRef.current?.click()} className="text-[13px] font-semibold text-[#000F36] focus:bg-slate-100 focus:text-[#000F36]">
                  <Upload className="w-4 h-4 mr-2 text-blue-500 shrink-0" /> Upload a resume
                  <span className="ml-auto text-[11px] font-medium text-slate-400">PDF, DOCX, TXT</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-400">Your saved resumes</DropdownMenuLabel>
                {saved === null && <div className="px-2 py-2 text-[12.5px] text-slate-500">Loading...</div>}
                {saved?.length === 0 && <div className="px-2 py-2 text-[12.5px] text-slate-500">No saved resumes yet.</div>}
                {saved?.map((r) => (
                  <DropdownMenuItem key={r.id} onClick={() => pickSaved(r)} className="text-[13px] focus:bg-slate-100 focus:text-[#000F36]">
                    <FileText className="w-4 h-4 mr-2 text-emerald-500 shrink-0" />
                    <span className="truncate">{r.title || r.file_name || "Untitled resume"}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Job: Job Library pick, or paste in the box */}
          {pickedJob ? (
            <span className={chipDone + " pr-1.5"} title={`Job selected: ${pickedJob.title}`}>
              <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="truncate">{pickedJob.title}{pickedJob.company ? ` · ${pickedJob.company}` : ""}</span>
              <button onClick={() => { setPickedJob(null); setJd(""); }} disabled={working} aria-label="Remove job" className="rounded p-0.5 text-slate-400 hover:text-slate-700">
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button disabled={working} className={hasJd ? chipDone : chipTodo}>
                  {hasJd ? <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <Briefcase className="w-3.5 h-3.5" />}
                  {hasJd ? "Pasted job" : "Choose Job"}
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-80 max-h-80 overflow-y-auto font-outfit">
                <DropdownMenuItem onClick={() => { setPickedJob(null); setTimeout(() => jdRef.current?.focus(), 50); toast({ title: "Paste the new job description", description: "Paste it into the box above. It is used for this run." }); }} className="text-[13px] font-semibold text-[#000F36] focus:bg-slate-100 focus:text-[#000F36]">
                  <Briefcase className="w-4 h-4 mr-2 text-amber-500 shrink-0" /> Add a new job: paste its description
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/qualification-wizard")} className="text-[13px] font-semibold text-[#000F36] focus:bg-slate-100 focus:text-[#000F36]">
                  <ClipboardList className="w-4 h-4 mr-2 text-emerald-500 shrink-0" /> Add a new job to my Job Library
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-400">Your saved jobs</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {savedJobs === null && <div className="px-2 py-2 text-[12.5px] text-slate-500">Loading...</div>}
                {savedJobs?.length === 0 && <div className="px-2 py-2 text-[12.5px] text-slate-500">No saved jobs yet. Paste a job description in the box.</div>}
                {savedJobs?.map((j) => (
                  <DropdownMenuItem key={j.id} onClick={() => pickJob(j)} className="text-[13px] focus:bg-slate-100 focus:text-[#000F36]">
                    <Briefcase className="w-4 h-4 mr-2 text-amber-500 shrink-0" />
                    <span className="truncate"><span className="font-semibold">{j.jobTitle}</span>{j.companyName ? <span className="text-slate-500"> · {j.companyName}</span> : null}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Documents */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={documents.length ? chipDone : chipTodo} disabled={working}>
                {documents.length ? <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <Files className="w-3.5 h-3.5" />}
                <span className="truncate">{documents.length
                  ? documents.map((id) => DOCUMENTS.find((d) => d.id === id)?.label ?? id).join(", ")
                  : "Choose Docs to Create"}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 font-outfit">
              <DropdownMenuLabel className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-400">Documents to create</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {DOCUMENTS.map((d) => {
                const lockedDoc = "deepOnly" in d && depth !== "deep";
                return (
                  <DropdownMenuCheckboxItem
                    key={d.id}
                    checked={documents.includes(d.id)}
                    disabled={lockedDoc}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={() => toggleDoc(d.id)}
                    className="text-[13px] justify-between focus:bg-slate-100 focus:text-[#000F36]"
                  >
                    <span>{d.label}</span>
                    {lockedDoc && <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400"><Lock className="w-3 h-3" /> Deep only</span>}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Design */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={designNeedsWord ? chipTodo : chipDone} disabled={working} title="The look of the finished resume">
                {designNeedsWord ? <FileText className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                {design === "original-format"
                  ? (designNeedsWord ? "My design: attach a Word file" : "My own design")
                  : "RDR Blue"}
                <ChevronDown className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-80 font-outfit">
              <DropdownMenuLabel className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-400">Resume design</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={design} onValueChange={(v) => setDesign(v as 'rdr-blue' | 'original-format')}>
                <DropdownMenuRadioItem value="rdr-blue" onSelect={(e) => e.preventDefault()} className="text-[13px] items-start focus:bg-slate-100 focus:text-[#000F36]">
                  <span><span className="font-semibold text-[#000F36]">RDR Blue</span><span className="block text-[12px] text-slate-500">The Results Driven Resumes design. Other colours on the results page.</span></span>
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="original-format" onSelect={(e) => e.preventDefault()} className="text-[13px] items-start focus:bg-slate-100 focus:text-[#000F36]">
                  <span><span className="font-semibold text-[#000F36]">My own design</span><span className="block text-[12px] text-slate-500">Rebuilds the new resume in the design of the Word (.docx) resume you attach.</span></span>
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
              {design === "original-format" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => designFileRef.current?.click()} className="text-[13px] font-semibold text-[#000F36] focus:bg-slate-100 focus:text-[#000F36]">
                    <Upload className="w-4 h-4 mr-2 text-blue-500 shrink-0" />
                    {resume?.docxUrl ? "Use a different Word file" : "Attach the Word (.docx) file"}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <input ref={designFileRef} type="file" accept=".docx" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) attachDesignFile(f); e.target.value = ""; }} />

          {/* Options */}
          <Popover>
            <PopoverTrigger asChild>
              <button className={optionsSet ? chipDone : chipOptional} disabled={working}>
                {optionsSet ? <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <Settings2 className="w-3.5 h-3.5 text-slate-500" />}
                Options
                <ChevronDown className="w-3 h-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[360px] p-0 font-outfit">
              <div className="p-4 space-y-4 max-h-[460px] overflow-y-auto">
                <div className="space-y-1.5">
                  <span className="text-[13px] font-semibold text-[#000F36]">Tailoring type</span>
                  <div className="grid grid-cols-2 gap-1.5">
                    {([["quick", "Quick", "Tailors straight from the job description."], ["deep", "Deep", "Builds a Tailoring Report on the company's pain points first."]] as const).map(([v, label, sub]) => (
                      <button key={v} type="button" onClick={() => setDepth(v as QuickStartDepth)}
                        className={`rounded-lg border px-2.5 py-2 text-left ${depth === v ? "border-[#000F36] bg-slate-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                        <span className="flex items-center gap-1 text-[12.5px] font-semibold text-[#000F36]">{depth === v && <Check className="w-3.5 h-3.5 text-emerald-600" />}{label}</span>
                        <span className="block text-[11.5px] leading-snug text-slate-500">{sub}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <span className="text-[13px] font-semibold text-[#000F36]">Job description type</span>
                  <div className="grid grid-cols-2 gap-1.5">
                    {([["Full JD", "Full JD", "Recommended. Uses only what the resume can prove."], ["Personalized JD", "Personalized JD", "Every item in the job description is confirmed as done."]] as const).map(([v, label, sub]) => (
                      <button key={v} type="button" onClick={() => setMode(v as QuickStartMode)}
                        className={`rounded-lg border px-2.5 py-2 text-left ${mode === v ? "border-[#000F36] bg-slate-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                        <span className="flex items-center gap-1 text-[12.5px] font-semibold text-[#000F36]">{mode === v && <Check className="w-3.5 h-3.5 text-emerald-600" />}{label}</span>
                        <span className="block text-[11.5px] leading-snug text-slate-500">{sub}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13px] font-semibold text-[#000F36]">Career highlights</span>
                  <select
                    value={highlightCount}
                    onChange={(e) => setHighlightCount(Number(e.target.value))}
                    className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px] text-[#000F36] outline-none"
                  >
                    {HIGHLIGHT_CHOICES.map((n) => <option key={n} value={n}>{n === 0 ? "None" : `${n} highlights`}</option>)}
                  </select>
                </div>
                <label className="flex items-center justify-between gap-3">
                  <span>
                    <span className="block text-[13px] font-semibold text-[#000F36]">Match score and analysis</span>
                    <span className="block text-[12px] text-slate-500">Before and after match scores.</span>
                  </span>
                  <Switch checked={generateAnalysis} onCheckedChange={setGenerateAnalysis} />
                </label>
                <label className="flex items-center justify-between gap-3">
                  <span>
                    <span className="block text-[13px] font-semibold text-[#000F36]">Auto-fix formatting</span>
                    <span className="block text-[12px] text-slate-500">Runs the layout fixers on the finished resume.</span>
                  </span>
                  <Switch checked={autoFix} onCheckedChange={setAutoFix} />
                </label>
                <div className="h-px bg-slate-200" />
                <div>
                  <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#000F36] mb-1.5">
                    <MessageSquare className="w-3.5 h-3.5 text-orange-500" /> Special instructions
                  </span>
                  <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={3}
                    placeholder="Anything the resume must include, avoid, or emphasize"
                    className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-[#000F36] placeholder:text-slate-400 outline-none focus:border-[#000F36]/40" />
                </div>
                <div>
                  <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#000F36] mb-1.5">
                    <ClipboardList className="w-3.5 h-3.5 text-violet-500" /> Client worksheets
                  </span>
                  <textarea value={newClientWorksheet} onChange={(e) => setNewClientWorksheet(e.target.value)} rows={3}
                    placeholder="Paste the New Client Worksheet"
                    className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-[#000F36] placeholder:text-slate-400 outline-none focus:border-[#000F36]/40 mb-2" />
                  <textarea value={quantWorksheet} onChange={(e) => setQuantWorksheet(e.target.value)} rows={3}
                    placeholder="Paste the Metrics Worksheet"
                    className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-[#000F36] placeholder:text-slate-400 outline-none focus:border-[#000F36]/40" />
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {busy && (
            <span className="inline-flex items-center gap-1.5 px-2 text-[12px] font-semibold text-slate-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> {busy === "reading" ? "Reading resume..." : "Loading resume..."}
            </span>
          )}

          <button
            onClick={submit}
            disabled={!locked && !ready}
            aria-label="Start tailoring"
            title={ready ? "Start tailoring" : missing.length ? `First ${missing.join(" and ")}` : "Working..."}
            className="ml-auto w-9 h-9 rounded-full flex items-center justify-center transition-colors bg-[#000F36] text-white hover:bg-[#13245A] disabled:bg-slate-200 disabled:text-slate-400"
          >
            {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" strokeWidth={2.5} />}
          </button>
        </div>
      </div>

      </div>
      {!working && (
        <div className="mt-3 min-h-[22px] text-center text-[12.5px]">
          {phase.phase === "error" ? (
            <span className="font-semibold text-red-600">{phase.message}</span>
          ) : missing.length ? (
            <span className="text-slate-500">To start, {missing.join(" and ")}.</span>
          ) : (
            <span className="font-semibold text-emerald-600">Ready. Press the arrow to tailor.</span>
          )}
        </div>
      )}
    </div>
  );
}
